import type { ReferenceTableHit } from "@/apis/reference-search/types";
import { Switch } from "@/toggle-ui-kit/control/switch";
import { ExternalLink } from "lucide-react";
import { useState } from "react";
import { HitDetail } from "./hit-detail";
import { MAPPING_STATUS_LABEL } from "./mapping-status";

const REPORT_BASE_LABEL = { consolidated: "연결", separate: "별도" } as const;

const mappingViewUrl = (hit: ReferenceTableHit) => {
  const q = new URLSearchParams({
    rceptNo: hit.id.rceptNo,
    attachmentCode: hit.id.attachmentCode,
    noteId: hit.id.noteId,
    subIndex: String(hit.id.subIndex),
  });
  return `/reference-search/view?${q.toString()}`;
};

/** 초록 = 매핑 있음(셀 수 함께), 회색 = 없는 이유. null(모름)은 표지를 달지 않는다. */
const MappingChip = ({ hit }: { hit: ReferenceTableHit }) => {
  if (!hit.xbrlMapping) return null;
  if (hit.xbrlMapping === "mapped") {
    return (
      <span className="caption-1-normal-medium rounded bg-emerald-50 px-1.5 py-0.5 text-emerald-700">
        {MAPPING_STATUS_LABEL.mapped}
        {hit.numericCells !== null && ` · 셀 ${hit.matchedCells}/${hit.numericCells}`}
      </span>
    );
  }
  return (
    <span className="caption-1-normal-medium rounded bg-neutral-100 px-1.5 py-0.5 text-label-assistive">
      {MAPPING_STATUS_LABEL[hit.xbrlMapping]}
    </span>
  );
};

const hitKey = (hit: ReferenceTableHit) =>
  `${hit.id.rceptNo}/${hit.id.attachmentCode}/${hit.id.noteId}/${hit.id.subIndex}`;

export const HitList = ({
  hits,
  elapsedMs,
  mappedOnly,
  onMappedOnlyChange,
}: {
  hits: ReferenceTableHit[] | null;
  elapsedMs: number | null;
  mappedOnly: boolean;
  onMappedOnlyChange: (value: boolean) => void;
}) => {
  const [openKey, setOpenKey] = useState<string | null>(null);

  return (
    <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-neutral-200 p-4">
      <div className="flex items-center justify-between">
        <h2 className="label-1-normal-bold text-label-neutral">결과</h2>
        <label className="flex items-center gap-2 caption-1-normal-medium text-label-neutral">
          <Switch size={24} active={mappedOnly} onChange={onMappedOnlyChange} />
          XBRL 매핑 있는 표만
        </label>
      </div>
      <div className="flex items-center justify-end">
        {hits && (
          <span className="caption-1-normal-medium text-label-assistive">
            상위 {hits.length}건
            {elapsedMs !== null && ` · ${(elapsedMs / 1000).toFixed(1)}초`}
          </span>
        )}
      </div>
      {hits === null ? (
        <p className="body-2-normal-medium text-label-assistive">
          왼쪽에 표를 붙여넣고 검색하면 여기에 유사한 주석 표가 나옵니다.
        </p>
      ) : hits.length === 0 ? (
        <p className="body-2-normal-medium text-label-assistive">
          비슷한 표를 찾지 못했습니다. 행 라벨이 포함되도록 표를 더 넓게 복사해 보세요.
        </p>
      ) : (
        <ol className="flex flex-col divide-y divide-neutral-200 rounded-md border border-neutral-200">
          {hits.map((hit, index) => {
            const key = hitKey(hit);
            const isOpen = openKey === key;
            return (
              <li key={key} className="flex flex-col">
                <button
                  type="button"
                  className={`grid grid-cols-[28px_minmax(0,1fr)_auto_120px] items-center gap-3 px-3 py-2 text-left hover:bg-neutral-50 ${
                    isOpen ? "bg-blue-50" : ""
                  }`}
                  onClick={() => setOpenKey(isOpen ? null : key)}
                >
                  <span className="caption-1-normal-medium tabular-nums text-label-assistive">
                    {index + 1}
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="body-2-normal-medium truncate text-label-normal">
                      {hit.noteTitle}
                    </span>
                    <span className="caption-1-normal-medium text-label-assistive">
                      {hit.companyName} · {REPORT_BASE_LABEL[hit.reportBase]} · {hit.year}{" "}
                      사업보고서
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    <MappingChip hit={hit} />
                    {hit.xbrlMapping === "mapped" && (
                      <a
                        className="caption-1-normal-medium inline-flex items-center gap-1 text-blue-600 hover:underline"
                        href={mappingViewUrl(hit)}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        XBRL 매핑 보기 <ExternalLink className="size-3" />
                      </a>
                    )}
                  </span>
                  <span className="flex items-center gap-2 justify-self-end">
                    <span
                      className="block h-1.5 rounded-full bg-blue-300"
                      style={{ width: `${Math.round(hit.score * 72)}px` }}
                    />
                    <span className="caption-1-normal-medium tabular-nums text-label-neutral">
                      {hit.score.toFixed(3)}
                    </span>
                  </span>
                </button>
                {isOpen && <HitDetail hit={hit} />}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
};
